import {Injectable} from '@angular/core';
import type {BootstrapResult} from '@app/core/interfaces';
import {Observable, of, Subject} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {CookieService} from 'ngx-cookie-service';

import {StoreService} from '@shared/services/store.service';
import {TokenService} from '@app/core/services/base/token.service';
import {WorkspaceApiService} from '@app/main/modules/teammates/api/workspace-api.service';
import type {WorkspaceResult} from '@app/main/modules/teammates/api/workspace-api.models';
import {WorkspaceType} from '@shared/enums/workspace.enum';
import {WORKSPACE_ID_COOKIE} from '@shared/constants/workspace.constants';
import {environment} from '@env/environment';

/**
 * Previous workspace when selection changed.
 * `undefined` means the call was a no-op (disabled / already selected / not assigned).
 */
export type WorkspaceSelectResult = WorkspaceResult | null | undefined;

@Injectable({
  providedIn: 'root',
})
export class WorkspaceService {
  private bootstrapRequestContextId: string | null = null;
  private pendingBootstrapRefetch = false;
  private readonly workspaceContextChanged = new Subject<void>();
  readonly workspaceContextChanged$ = this.workspaceContextChanged.asObservable();

  constructor(
    private storeService: StoreService,
    private tokenService: TokenService,
    private workspaceApiService: WorkspaceApiService,
    private cookieService: CookieService,
  ) {
  }

  isAssignedWorkspace(workspace: WorkspaceResult | null = this.storeService.getCurrentWorkspace()): boolean {
    return !!workspace && workspace.type === WorkspaceType.Assigned && !workspace.isOwner;
  }

  getPersonalWorkspace(list: WorkspaceResult[]): WorkspaceResult | null {
    return list.find((item) => item.type === WorkspaceType.Personal || item.isOwner) ?? null;
  }

  /**
   * Restores Assigned workspace from cookie (same persistence as login tokens)
   * so the first bootstrap can send X-Workspace-Id after a full reload.
   */
  prepareInitialContext(): void {
    const persistedId = this.readPersistedWorkspaceId();
    if (persistedId) {
      this.storeService.setCurrentWorkspace(this.createAssignedStub(persistedId));
      this.bootstrapRequestContextId = this.normalizeContextId(persistedId);
      return;
    }

    this.storeService.setCurrentWorkspace(null);
    this.bootstrapRequestContextId = this.normalizeContextId(this.getActorUserId());
  }

  initAfterBootstrap(bootstrap: BootstrapResult): void {
    const workspaces = bootstrap.workspaces ?? [];
    const personal = this.getPersonalWorkspace(workspaces);
    const current = this.storeService.getCurrentWorkspace();
    let selected = current
      ? workspaces.find((item) => item.id === current.id) ?? null
      : null;

    if (!selected || selected.isDisabled) {
      selected = personal;
    }

    this.storeService.setCurrentWorkspace(selected);
    this.syncPersistedWorkspace(selected);

    const resolvedId = this.normalizeContextId(selected?.id ?? this.getActorUserId());
    this.pendingBootstrapRefetch = resolvedId !== this.bootstrapRequestContextId;
    this.bootstrapRequestContextId = resolvedId;
  }

  needsBootstrapRefetchAfterInit(): boolean {
    return this.pendingBootstrapRefetch;
  }

  clearPendingBootstrapRefetch(): void {
    this.pendingBootstrapRefetch = false;
  }

  notifyWorkspaceContextChanged(): void {
    this.workspaceContextChanged.next();
  }

  /**
   * Updates in-memory selection only. Caller must refetch bootstrap when this returns a defined value.
   * @returns previous selection when changed; `undefined` when no-op
   */
  selectWorkspace(workspace: WorkspaceResult): WorkspaceSelectResult {
    if (!workspace || workspace.isDisabled) {
      return undefined;
    }

    const current = this.storeService.getCurrentWorkspace();
    if (current?.id === workspace.id) {
      return undefined;
    }

    this.applySelection(workspace);
    return current;
  }

  /**
   * Switches to Personal workspace selection only. Caller must refetch bootstrap when changed.
   * @returns previous selection when changed; `undefined` when no-op
   */
  selectPersonal(): WorkspaceSelectResult {
    if (!this.isAssignedWorkspace()) {
      return undefined;
    }

    const current = this.storeService.getCurrentWorkspace();
    const list = this.storeService.getBaseData()?.workspaces ?? [];
    const personal = this.getPersonalWorkspace(list);

    if (personal) {
      this.applySelection(personal);
    } else {
      this.clearSelectionToActor();
    }

    return current;
  }

  restoreSelection(workspace: WorkspaceResult | null): void {
    if (workspace) {
      this.applySelection(workspace);
      return;
    }

    this.clearSelectionToActor();
  }

  /**
   * Refreshes workspace list from API and validates current selection.
   * @returns true when bootstrap refetch is required (e.g. switched back to Personal)
   */
  syncWorkspacesList(): Observable<boolean> {
    return this.workspaceApiService.getWorkspaces().pipe(
      map((res) => res.result ?? []),
      map((workspaces) => this.updateWorkspacesList(workspaces)),
    );
  }

  /**
   * Updates bootstrap workspaces and validates selection against the new list.
   * @returns true when bootstrap refetch is required
   */
  updateWorkspacesList(workspaces: WorkspaceResult[]): boolean {
    const baseData = this.storeService.getBaseData();
    if (baseData) {
      this.storeService.setBaseData({...baseData, workspaces});
    }
    return !this.validateCurrentSelection(workspaces);
  }

  /**
   * @returns true when current selection remains valid without bootstrap refetch
   */
  validateCurrentSelection(workspaces: WorkspaceResult[]): boolean {
    const current = this.storeService.getCurrentWorkspace();
    const personal = this.getPersonalWorkspace(workspaces);

    if (!current || current.type === WorkspaceType.Personal || current.isOwner) {
      if (personal) {
        this.storeService.setCurrentWorkspace(personal);
      }
      this.clearPersistedWorkspaceId();
      return true;
    }

    const match = workspaces.find((item) => item.id === current.id);
    if (!match || match.isDisabled) {
      if (personal) {
        this.applySelection(personal);
      } else {
        this.clearSelectionToActor();
      }
      return false;
    }

    if (
      match.name !== current.name
      || match.isDisabled !== current.isDisabled
      || match.hasSeen !== current.hasSeen
    ) {
      this.storeService.setCurrentWorkspace(match);
    }
    this.persistWorkspaceId(match.id);
    return true;
  }

  getUnseenWorkspaces(workspaces: WorkspaceResult[] = this.storeService.getBaseData()?.workspaces ?? []): WorkspaceResult[] {
    return workspaces.filter((item) => item.hasSeen === false);
  }

  /**
   * Marks the given Assigned ids as seen (one request). No-op when ids is empty.
   * Applies hasSeen locally first so the discovery tooltip can hide immediately.
   */
  markWorkspacesSeen(workspaceIds: string[]): Observable<boolean> {
    const ids = [...new Set(workspaceIds.filter((id) => !!id))];
    if (ids.length === 0) {
      return of(true);
    }

    this.applyHasSeen(ids, true);

    return this.workspaceApiService.markSeen({workspaceIds: ids}).pipe(
      map(() => true),
      catchError((error) => {
        this.applyHasSeen(ids, false);
        throw error;
      }),
    );
  }

  /** Clears assigned selection and scopes subsequent requests to the actor user. */
  private clearSelectionToActor(): void {
    this.storeService.setCurrentWorkspace(null);
    this.bootstrapRequestContextId = this.normalizeContextId(this.getActorUserId());
    this.clearPersistedWorkspaceId();
  }

  private syncPersistedWorkspace(workspace: WorkspaceResult | null): void {
    if (this.isAssignedWorkspace(workspace) && workspace?.id) {
      this.persistWorkspaceId(workspace.id);
      return;
    }
    this.clearPersistedWorkspaceId();
  }

  private persistWorkspaceId(workspaceId: string): void {
    this.clearPersistedWorkspaceId();
    this.cookieService.set(
      WORKSPACE_ID_COOKIE,
      workspaceId,
      {
        path: '/',
        domain: environment.cookieDomain,
        secure: environment.cookieSecure,
        sameSite: environment.cookieSameSite as 'Lax' | 'None' | 'Strict' | undefined,
      },
    );
  }

  private readPersistedWorkspaceId(): string | null {
    const value = this.cookieService.get(WORKSPACE_ID_COOKIE)?.trim();
    return value || null;
  }

  private clearPersistedWorkspaceId(): void {
    const domains = [
      environment.cookieDomain,
      window.location.hostname,
      undefined,
    ];

    domains.forEach((domain) => {
      try {
        this.cookieService.delete(WORKSPACE_ID_COOKIE, '/', domain);
      } catch {
      }
    });
  }

  /** Minimal Assigned workspace so the HTTP interceptor can send X-Workspace-Id before bootstrap. */
  private createAssignedStub(id: string): WorkspaceResult {
    return {
      id,
      name: '',
      type: WorkspaceType.Assigned,
      masterAccountId: 0,
      masterMobile: '',
      isOwner: false,
      isDisabled: false,
      hasSeen: true,
    };
  }

  private applyHasSeen(workspaceIds: string[], hasSeen: boolean): void {
    const idSet = new Set(workspaceIds);
    const baseData = this.storeService.getBaseData();
    if (baseData?.workspaces) {
      this.storeService.setBaseData({
        ...baseData,
        workspaces: baseData.workspaces.map((item) =>
          idSet.has(item.id) ? {...item, hasSeen} : item,
        ),
      });
    }

    const current = this.storeService.getCurrentWorkspace();
    if (current && idSet.has(current.id)) {
      this.storeService.setCurrentWorkspace({...current, hasSeen});
    }
  }

  private applySelection(workspace: WorkspaceResult): void {
    this.storeService.setCurrentWorkspace(workspace);
    this.bootstrapRequestContextId = this.normalizeContextId(workspace.id);
    this.syncPersistedWorkspace(workspace);
  }

  private normalizeContextId(id: string | null | undefined): string | null {
    const actorUserId = this.getActorUserId();
    if (!id || (actorUserId && id === String(actorUserId))) {
      return actorUserId ? String(actorUserId) : null;
    }
    return id;
  }

  private getActorUserId(): string | null {
    try {
      const token = this.tokenService.getAccessToken();
      if (!token) {
        return null;
      }
      return this.tokenService.decodeToken()?.UserId ?? null;
    } catch {
      return null;
    }
  }
}
